import { IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText } from "@material-ui/core";
import Axios from "axios";
import { observer } from "mobx-react-lite";
import React, { Fragment, useState, useEffect } from "react";
import CustomInput from "../../components/CustomInput";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { deepClone } from "../../helpers/objectHelper";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import EventLabel from "../../components/EventLabel";
import { toJS } from "mobx";

export const Discount = observer((props) => {
  const [code, setCode] = useState("");
  const [isValid, setIsValid] = useState(null);
  const [validCodes, setValidCodes] = useState([]);
  const [earlyBirdLoaded, setEarlyBirdLoaded] = useState(false);
  const [orderDiscountsLoaded, setOrderDiscountsLoaded] = useState(false);
  const [showMaxDiscountValidationMessage, setShowMaxDiscountValidationMessage] = useState(false);
  const { orders, activePersonNo, settings, updateOrder, environment, sqlStateNo } = props;
  const { hideOnChangeLink, maxDiscounts, hidden, showEarlyBird = false, autoCodes, showAutoCodes = true } = settings;

  useEffect(() => {
    fetchEarlyBird();
    fetchOrderDiscounts();
  }, []);

  useEffect(() => {
    fetchEarlyBird();
    fetchOrderDiscounts();
  }, [activePersonNo]);

  useEffect(() => {
    if (earlyBirdLoaded && orderDiscountsLoaded) applyAutoCodes();
  }, [earlyBirdLoaded, orderDiscountsLoaded]);

  const fetchEarlyBird = () => {
    if (earlyBirdLoaded) setEarlyBirdLoaded(false);
    Axios.get(`./api/discount/${environment.eventId}`).then(({ data }) => {
      if (data) {
        let order = deepClone(orders[activePersonNo]);
        if (!Array.isArray(order.discounts)) {
          order.discounts = [];
        }
        let codesToAdd = data.filter((d) => {
          if (!order.discounts.find((o) => o.discountId == d.discountId)) {
            return true;
          }
        });
        if (codesToAdd.length > 0) {
          order.discounts = [...order.discounts, ...codesToAdd];
          setValidCodes([...order.discounts]);
          updateOrder(order);
        } else {
          setValidCodes([...order.discounts]);
        }
        setEarlyBirdLoaded(true);
      }
    });
  };

  const fetchOrderDiscounts = () => {
    if (orderDiscountsLoaded) setOrderDiscountsLoaded(false);
    if (orders && orders.length > 0 && orders[activePersonNo].badgeNo) {
      Axios.get(`./api/discount/mydiscounts/${orders[activePersonNo].orderId}`).then(({ data }) => {
        if (data) {
          setValidCodes([...validCodes, ...data]);
        }
        setOrderDiscountsLoaded(true);
      });
    } else {
      setOrderDiscountsLoaded(true);
    }
  };

  const applyAutoCodes = async () => {
    if (autoCodes && Array.isArray(autoCodes)) {
      const codesToApply = [];
      for (const discount of autoCodes) {
        const { data } = await Axios.get(`./api/discount/validate/${environment.eventId}?code=${discount.value}`);
        if (data) {
          if(Array.isArray(data)){
            data.forEach((discount) => {
              if (!codesToApply.find((d) => d.code == discount.code && d.targetEventId == discount.targetEventId)) {
                codesToApply.push(discount);
              }
            });
          }
          else{
            codesToApply.push(data);
          }
        }
      }
      if (codesToApply.length > 0) {
        let order = deepClone(orders[activePersonNo]);
        if (!Array.isArray(order.discounts)) {
          order.discounts = [];
        }
        codesToApply.forEach((discount) => {
          if (!order.discounts.find((d) => d.code == discount.code && d.targetEventId == discount.targetEventId)) order.discounts.push(discount);
        });
        setValidCodes([...order.discounts]);
        updateOrder(order, activePersonNo);
      }
    }
  };

  const validateCode = () => {
    const orderDiscounts = orders.reduce((acc, o) => [...acc, ...(o.discounts ?? [])], []).filter(({ code: _code }) => _code === code)?.length;
    if (maxDiscounts != null && maxDiscounts > 0 && orders[activePersonNo] && Array.isArray(orders[activePersonNo].discounts) && orders[activePersonNo].discounts.length == maxDiscounts) {
      return setShowMaxDiscountValidationMessage(true);
    }
    setShowMaxDiscountValidationMessage(false);
    Axios.get(`./api/discount/validate/${environment.eventId}?code=${code}&sqlStateNo=${sqlStateNo}&count=${orderDiscounts}`)
      .then(({ data }) => {
        const discounts = data;
        if (discounts?.length) {
          const order = deepClone(orders[activePersonNo]);

          if (!Array.isArray(order.discounts)) {
            order.discounts = [];
          }

          if (order.discounts.find((d) => discounts.some((discount) => d.code == discount.code))) return;

          order.discounts = [...order.discounts, ...discounts];

          setCode("");
          setIsValid(true);
          setValidCodes([...validCodes, ...discounts]);

          updateOrder(order, activePersonNo);
        } else {
          setIsValid(false);
        }
      })
      .catch((e) => {
        setShowMaxDiscountValidationMessage(true);
      });
  };

  const inputOnChange = (code) => {
    setIsValid(null);
    setShowMaxDiscountValidationMessage(false);
    setCode(code);
  };

  const deleteDiscount = (code) => {
    let order = deepClone(orders[activePersonNo]);

    if (!order.discounts.find((d) => d.code == code)) return;

    order.discounts = order.discounts.filter((d) => d.code != code);

    const newCodes = validCodes.filter((v) => v.code != code);

    setValidCodes(newCodes);
    updateOrder(order, activePersonNo);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && code) {
      validateCode();
    }
  };

  if (orders[activePersonNo] && orders[activePersonNo]?.badgeNo && hideOnChangeLink) return null;
  if (hidden) return null;

  return (
    <div className="discount-grid">
      <CustomInput
        prefixClassName="cms-field"
        discount={true}
        value={code}
        onChange={(e) => inputOnChange(e.target.value)}
        onKeyDown={onKeyDown}
        noMargin={true}
        noLabel={true}
        appendButton={
          <button disabled={!code} onClick={validateCode} className="cms-button apply-discount-btn append-button ">
            <EventLabel name="ApplyCode" groupName="Cms.Discount" />
          </button>
        }
      />
      <div style={{ width: "100%" }}>
        {isValid == false || showMaxDiscountValidationMessage ? (
          <Alert severity="error">{showMaxDiscountValidationMessage ? <EventLabel name="MaxDiscountsReached" groupName="Cms.Discount" /> : <EventLabel name="NoDiscountFound" groupName="Cms.Discount" />}</Alert>
        ) : null}
      </div>
      <List>
        {validCodes.map((v, i) => {
          const { code, value, name, valueType } = v;
          if (!showEarlyBird && !code) return null;
          if (!showAutoCodes && autoCodes.some((ac) => ac.value == code)) return null;
          return (
            <Fragment key={code + i}>
              <ListItem className="applied-discount-item" style={{ background: "rgb(237, 247, 237)", padding: "0px 6px", borderRadius: 5, margin: "5px 0px", border: "1px solid #4caf50" }}>
                <ListItemIcon>
                  <CheckCircleOutlineIcon style={{ fontSize: 32, color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span style={{ color: "rgb(30, 70, 32)", fontSize: "0.875rem" }}>
                      <strong>{code || name}</strong>
                    </span>
                  }
                  secondary={
                    <span style={{ color: "rgb(30, 70, 32, 0.7)", display: "flex", flexDirection: "row" }}>
                      <EventLabel name="DiscountOff" groupName="Cms.Discount" textOnly /> {`${value}${valueType == "Percent" ? "%" : " " + (environment.currency || "SEK")}`}
                    </span>
                  }
                />
                <ListItemSecondaryAction>
                  {code ? (
                    <IconButton onClick={() => deleteDiscount(code)}>
                      <CloseIcon />
                    </IconButton>
                  ) : null}
                </ListItemSecondaryAction>
              </ListItem>
            </Fragment>
          );
        })}
      </List>
    </div>
  );
});
